import { useAuth } from '@Auth/useAuth';
import { FlexBox } from '@common/FlexBox';
import { Popup } from '@common/Popup';
import { Settings } from '@mui/icons-material';
import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  Paper,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { memo } from 'react';
import { useNavigate } from 'react-router-dom';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

const MenuButton = styled(IconButton)`
  border-radious: 8px;
`;

export const SettingsPopup = memo(() => {
  const { supabaseUser, signOut } = useAuth();
  // console.log(supabaseUser);
  const avatarUrl = supabaseUser?.user_metadata?.avatar_url || '';
  // const userName = supabaseUser?.user_metadata?.name || 'Unknown User';
  const userEmail = supabaseUser?.email || 'Unknown User';

  const theme = useTheme();
  const navigate = useNavigate();
  return (
    <Box>
      <Popup
        top
        right
        anchorElement={(
          onClick: (event: React.MouseEvent<HTMLElement>) => void
        ) => (
          <MenuButton onClick={onClick}>
            <Settings />
          </MenuButton>
        )}
      >
        <Paper
          elevation={3}
          sx={{ width: 212, backgroundColor: theme.palette.background.default }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <FlexBox alignCenter gap={'12px'} style={{ padding: '8px 16px' }}>
              <StyledBadge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                variant="dot"
              >
                {avatarUrl ? (
                  <Avatar src={avatarUrl} />
                ) : (
                  <Avatar sx={{ backgroundColor: '#fd7e97' }}>
                    {userEmail.charAt(0).toUpperCase()}
                  </Avatar>
                )}
              </StyledBadge>
              <Typography
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '150px',
                }}
                variant="subtitle1"
              >
                {userEmail}
              </Typography>
            </FlexBox>
            <Divider />
            <OptionsContainer>
              <SideMenuButton
                onClick={() => navigate('/settings/user-preferences')}
              >
                Settings
              </SideMenuButton>
              <SideMenuButton onClick={signOut}>Log out</SideMenuButton>
            </OptionsContainer>
          </Box>
        </Paper>
      </Popup>
    </Box>
  );
});

const OptionsContainer = styled(
  'div',
  {}
)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '8px',
}));

const SideMenuButton = styled('div', {
  shouldForwardProp: (prop) => prop !== 'active', // Ensures the 'active' prop is not passed to the DOM element
})(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '4px 12px',
  height: '28px',
  fontSize: '12px',
  color: theme.palette.text.secondary,
  transitionProperty:
    'color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter, -webkit-backdrop-filter',
  transitionTimingFunction: 'cubic-bezier(.4,0,.2,1)',
  transitionDuration: '.15s',
  fontWeight: 500,
  borderRadius: '4px',
  backgroundColor: 'transparent',
  '&:hover': {
    color: theme.palette.text.primary,
    cursor: 'pointer',
    background: theme.palette.action.hover,
  },
}));
